'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import PropTypes from 'prop-types';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializationError, setInitializationError] = useState(null);
  const [supabase, setSupabase] = useState(null);

  // Fetch user profile from user_profiles table with timeout and retry
  const fetchUserProfile = useCallback(async (userId, supabaseClient, retryCount = 0) => {
    if (!userId || !supabaseClient) {
      setUserProfile(null);
      return null;
    }

    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 10000; // Increased to 10 seconds

    try {
      // Create abort controller for better timeout handling
      const controller = new AbortController();
      let timeoutId = setTimeout(() => controller?.abort(), TIMEOUT_MS);

      try {
        const { data: profile, error } = await supabaseClient
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.maybeSingle()
          ?.abortSignal?.(controller?.signal);

        clearTimeout(timeoutId);

        if (error) {
          console.error('Error fetching user profile:', error);
          // If RLS error, try to get user metadata as fallback
          if (error?.message?.includes('permission') || error?.message?.includes('RLS')) {
            console.log('⚠️ RLS might be blocking profile access, using user metadata as fallback');
          }
          setUserProfile(null);
          return null;
        }

        if (profile) {
          console.log('✅ User profile loaded:', profile?.email, '| Role:', profile?.role);
          setUserProfile(profile);
          return profile;
        } else {
          console.log('ℹ️ No user profile found for user:', userId);
          setUserProfile(null);
          return null;
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        // Handle abort/timeout
        if (fetchError?.name === 'AbortError') {
          if (retryCount < MAX_RETRIES) {
            console.warn(`⚠️ Profile fetch timeout, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            return fetchUserProfile(userId, supabaseClient, retryCount + 1);
          } else {
            console.error('❌ Profile fetch timeout after retries, continuing without profile');
            setUserProfile(null);
            return null;
          }
        }
        
        throw fetchError;
      }
    } catch (err) {
      console.error('Exception fetching user profile:', err?.message);
      // On error, set profile to null but don't block auth flow
      setUserProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Initialize Supabase client
    try {
      const client = createClient();
      setSupabase(client);
    } catch (error) {
      console.error('Failed to initialize Supabase client:', error);
      setInitializationError(error?.message || 'Failed to initialize authentication');
      setLoading(false);
      return;
    }
  }, []);

  useEffect(() => {
    if (!supabase) return;

    let mounted = true;
    let timeoutId;

    // Get initial session
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
          error: sessionError
        } = await supabase?.auth?.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          console.log('🔔 Auth event: INITIAL_SESSION | Has session:', !!session);
          if (session?.user) {
            console.log('✅ Session user found:', session?.user?.email);
            setUser(session?.user);
            // Fetch user profile
            await fetchUserProfile(session?.user?.id, supabase);
          } else {
            console.log('ℹ️ No initial session found');
            setUser(null);
            setUserProfile(null);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
      }
    };

    // Timeout fallback - if auth doesn't initialize within 5 seconds, stop loading
    timeoutId = setTimeout(() => {
      if (mounted && loading) {
        console.warn('⚠️ Auth initialization timeout - stopping loading state');
        setLoading(false);
      }
    }, 5000);

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log('🔔 Auth event:', event, '| Has session:', !!session);

      // ✅ CRITICAL FIX: Only clear user on explicit sign-out
      // Don't clear session on navigation or initial load events
      if (event === 'SIGNED_OUT') {
        console.log('❌ User signed out - clearing session');
        setUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      // Handle signed-in state
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('✅ User signed in:', session?.user?.email);
        setUser(session?.user);
        // Fetch user profile
        await fetchUserProfile(session?.user?.id, supabase);
        setLoading(false);
        return;
      }

      // Handle initial session without clearing existing user
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          console.log('✅ Initial session found:', session?.user?.email);
          setUser(session?.user);
          // Fetch user profile
          await fetchUserProfile(session?.user?.id, supabase);
        } else {
          console.log('ℹ️ No initial session, preserving current state');
          // ✅ Don't clear existing user on initial session check
        }
        setLoading(false);
        return;
      }

      // Handle token refresh
      if (event === 'TOKEN_REFRESHED' && session?.user) {
        console.log('🔄 Token refreshed for:', session?.user?.email);
        setUser(session?.user);
        // Refresh profile too in case it changed
        await fetchUserProfile(session?.user?.id, supabase);
        return;
      }

      // Handle user update
      if (event === 'USER_UPDATED' && session?.user) {
        console.log('🔄 User updated:', session?.user?.email);
        setUser(session?.user);
        // Refresh profile
        await fetchUserProfile(session?.user?.id, supabase);
        return;
      }

      // For any other events, only update if we have a valid session
      if (session?.user) {
        console.log('ℹ️ Session updated:', event);
        setUser(session?.user);
      } else {
        console.log('ℹ️ No active session');
      }

      setLoading(false);
    });

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      subscription?.unsubscribe();
    };
  }, [supabase, fetchUserProfile]);

  const signIn = async (email, password) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      console.log('✅ Sign in successful:', data?.user?.email);

      // Immediately fetch user profile to get role for redirection
      let profile = null;
      if (data?.user?.id) {
        profile = await fetchUserProfile(data?.user?.id, supabase);
      }

      return { data, profile, error: null };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      return { data: null, profile: null, error };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });

      if (error) throw error;

      console.log('✅ Sign up successful:', data?.user?.email);
      return { data, error: null };
    } catch (error) {
      console.error('❌ Sign up error:', error);
      return { data: null, error };
    }
  };

  const signOut = (redirectToLogin = true) => {
    console.log('🔄 Signing out...');

    // Clear all storage and cookies
    if (typeof window !== 'undefined') {
      console.log('🧹 Clearing all auth data...');

      // Clear localStorage
      try {
        localStorage.clear();
        console.log('✅ localStorage cleared');
      } catch (e) {
        console.warn('Could not clear localStorage:', e);
      }

      // Clear sessionStorage
      try {
        sessionStorage.clear();
        console.log('✅ sessionStorage cleared');
      } catch (e) {
        console.warn('Could not clear sessionStorage:', e);
      }

      // Clear all cookies
      try {
        document.cookie?.split(';')?.forEach(cookie => {
          const name = cookie?.split('=')?.[0]?.trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        console.log('✅ cookies cleared');
      } catch (e) {
        console.warn('Could not clear cookies:', e);
      }
    }

    // Clear state
    setUser(null);
    setUserProfile(null);
    console.log('✅ State cleared');

    // Sign out from Supabase (don't await - fire and forget)
    if (supabase) {
      supabase?.auth?.signOut({ scope: 'global' })?.then(() => {
        console.log('✅ Supabase sign out complete');
      })?.catch((err) => {
        console.warn('Supabase sign out error:', err);
      });
    }

    // IMMEDIATE redirect - don't wait for anything
    if (typeof window !== 'undefined' && redirectToLogin) {
      console.log('🚀 Redirecting NOW to /login...');
      window.location.href = '/login';
    }

    return { error: null };
  };

  // Manual refresh of user profile
  const refreshProfile = useCallback(async () => {
    if (user?.id && supabase) {
      return await fetchUserProfile(user?.id, supabase);
    }
    return null;
  }, [user?.id, supabase, fetchUserProfile]);

  // Force re-authentication - clears session and returns email for re-login
  const forceReAuth = useCallback(async () => {
    console.log('🔄 Force re-auth initiated...');
    
    // Store user email before clearing
    const userEmail = user?.email;
    
    if (!userEmail) {
      console.warn('⚠️ No user email found for re-auth');
      return { email: null, error: 'No user email found' };
    }

    try {
      // Sign out without redirect
      await signOut(false);
      
      console.log('✅ Force re-auth complete - session cleared');
      return { email: userEmail, error: null };
    } catch (error) {
      console.error('❌ Force re-auth error:', error);
      return { email: userEmail, error: error?.message || 'Failed to clear session' };
    }
  }, [user?.email, signOut]);

  const value = {
    user,
    userProfile,
    loading,
    initializationError,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    forceReAuth,
    supabase
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes?.node?.isRequired
};
